import type { ReactNode } from 'react';
import { compassAreas, compassAxis, type CompassSeat } from './conceptualHands';

/**
 * Two hands placed round the vulnerability board by compass seat (client, 2026-10-05): North above
 * the board and South below it, West left of it and East right of it — the picture of the table
 * the Counting page draws.
 *
 * <p>Conceptual Problems only (the data-editing pages). The practice pages hold one hand and keep
 * the shared `SeatBoardLayout`; this page used it too, for the first hand, and let the second
 * follow in a wrapping row — east of the board, or under it with the first, wherever the column
 * was narrow. A grid puts each seat in its own area instead, so the placement no longer depends on
 * the width.</p>
 *
 * <p>The areas are built from the seats present: a North/South pair is one column of three rows,
 * an East/West pair one row of three columns. A mixed pair (none in the data today) gets the full
 * compass. `data-axis` names which, so the page's stylesheet can size the hands for it
 * (styles/data-editing.css).</p>
 *
 * <p><b>The middle is never empty.</b> Where the problem records no vulnerability, a partition line
 * stands where the board would (client, 2026-10-05): without it two hands one gap apart read as
 * one block of cards.</p>
 *
 * <p>The hands stay in the order given — the seat being asked first, which is the order a screen
 * reader meets them — whatever the picture. Each hand sits in a `seat-board` with its seat in
 * `data-seat`, the pair the shared phone rules key on to lay a North/South hand's suits out in a
 * row (index.css), so this layout inherits them unchanged.</p>
 */

export interface CompassHand {
  seat: CompassSeat;
  /** Heading shown directly above the hand. */
  label: ReactNode;
  hand: ReactNode;
  /** The test id the hand's wrapper carries (heading and cards together). */
  testId?: string;
}

export interface CompassBoardLayoutProps {
  hands: CompassHand[];
  /** The vulnerability board, or null where the problem records none (a partition line then). */
  board: ReactNode;
  /** Heading class, so the headings match the page's other seat headings exactly. */
  headingClassName: string;
}

const AREA: Record<CompassSeat, string> = { N: 'n', E: 'e', S: 's', W: 'w' };

export function CompassBoardLayout({ hands, board, headingClassName }: CompassBoardLayoutProps) {
  const seats = hands.map((h) => h.seat);
  const axis = compassAxis(seats);
  return (
    <div
      className="compass-board grid items-center justify-items-center gap-3 xnarrow:gap-1.5"
      style={{ gridTemplateAreas: compassAreas(seats) }}
      data-testid="compass-board-layout"
      data-seats={seats.join('')}
      data-axis={axis}
    >
      {hands.map((h) => (
        <div
          key={h.seat}
          className="seat-board flex min-w-0 flex-col items-center"
          style={{ gridArea: AREA[h.seat] }}
          data-seat={h.seat}
          data-testid={h.testId}
        >
          <h2 className={headingClassName}>{h.label}</h2>
          {h.hand}
        </div>
      ))}
      {board ? (
        <div className="compass-board-board shrink-0" style={{ gridArea: 'b' }}>
          {board}
        </div>
      ) : (
        // A line across the gap a North/South pair leaves, down the gap an East/West pair leaves;
        // its margins add to the grid gap, so the hands also sit further apart.
        <div
          className={`compass-divider bg-line-strong ${
            axis === 'ew' ? 'mx-2 w-px self-stretch' : 'my-2 h-px w-full justify-self-stretch'
          }`}
          style={{ gridArea: 'b' }}
          aria-hidden="true"
          data-testid="compass-divider"
        />
      )}
    </div>
  );
}
